import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Shared ALL-CAPS subsection label used across Section II.
 * Keep typography here so Narrative / Timeline / Assumptions cannot drift.
 */
export const SECTION_II_LABEL_CLASS =
  'block font-public-sans text-[12px] font-bold uppercase tracking-[0.05em] text-muted-foreground';

/**
 * Header row: label + optional action, vertically centered, one right edge.
 * `min-h-8` matches Button `size="sm"` so a bordered AI control and a
 * text-style Edit occupy the same vertical slot.
 */
export const SECTION_II_HEADER_ROW_CLASS =
  'section-ii-header-row flex min-h-8 items-center justify-between gap-3';

/**
 * Action slot — right-justified, no extra margin that would ragged the column.
 */
export const SECTION_II_HEADER_ACTION_CLASS =
  'section-ii-header-action flex shrink-0 items-center justify-end gap-2';

/**
 * Pair with `<Button size="sm">` so every header action shares `h-8 px-3`.
 * Color/weight only — do not override height or horizontal padding here.
 */
export const SECTION_II_HEADER_ACTION_BUTTON_CLASS =
  'section-ii-header-action-btn';

export interface SectionIIHeaderRowProps {
  label: ReactNode;
  /** Right-column control(s). Omit when the subsection has no action. */
  action?: ReactNode;
  className?: string;
}

/**
 * Section II subsection header. Wrap the ALL-CAPS label and the right-side
 * action so Narrative / Timeline / Assumptions / Project Details share one
 * action column. Future Narrative rewrites should keep this wrapper.
 */
export function SectionIIHeaderRow({
  label,
  action,
  className,
}: SectionIIHeaderRowProps) {
  return (
    <div
      className={cn(SECTION_II_HEADER_ROW_CLASS, className)}
      data-testid="section-ii-header-row"
    >
      <label className={SECTION_II_LABEL_CLASS}>{label}</label>
      {action != null ? (
        <div
          className={SECTION_II_HEADER_ACTION_CLASS}
          data-testid="section-ii-header-action"
        >
          {action}
        </div>
      ) : null}
    </div>
  );
}

export interface SectionIILabeledRuleProps {
  label: ReactNode;
  /** Muted ink (Project Details). Default is primary (Administration). */
  muted?: boolean;
  className?: string;
}

/**
 * Founder pick 2026-09-12 Option A — labeled hairline inside II. Project
 * Summary. Administration uses primary ink; Project Details uses muted.
 * Still a divider, not a new outer card.
 */
export function SectionIILabeledRule({
  label,
  muted = false,
  className,
}: SectionIILabeledRuleProps) {
  return (
    <div
      className={cn('flex items-center gap-3', className)}
      data-testid="section-ii-labeled-rule"
      role="separator"
      aria-label={typeof label === 'string' ? label : undefined}
    >
      <span aria-hidden="true" className="h-px min-w-0 flex-1 bg-outline-variant" />
      <span
        className={cn(
          SECTION_II_LABEL_CLASS,
          muted ? 'text-muted-foreground' : 'text-primary',
        )}
      >
        {label}
      </span>
      <span aria-hidden="true" className="h-px min-w-0 flex-1 bg-outline-variant" />
    </div>
  );
}
